import type { KgInsight } from '@api/api/kg-insights';
import { Button } from '@design/components/button/Button';
import { EXPANDED_VALUE } from '@terminal/components/expandable-drawer/ExpandableDrawer';
import { InsightDrawerHeaderContent } from '@terminal/components/insight-drawer-header/InsightDrawerHeaderContent';
import { useQueryParams } from '@terminal/hooks/use-query-params/useQueryParams';
import { useTrackEmailDrawerOpen } from '@terminal/hooks/use-track-email-drawer-open/useTrackEmailDrawerOpen';
import type { CatalystMarketReactionChartHeaderVariant } from '@terminal/modules/market-reaction-charts/types';
import { queryKeys } from '@terminal/routes/query-params';
import {
  POSTHOG_TRACKING_SOURCES,
  type PosthogTrackingSource,
} from '@terminal/services/tracking/posthog/constants';
import { usePosthogEvents } from '@terminal/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { CardType } from '@terminal/views/insight-feed/components/insight-feed-card/InsightFeedCard';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import {
  getCatalystEmailDrawerTracking,
  getCatalystTrackingKey,
} from '../../utils/catalyst-widget-utils';
import { KgInsightPreview } from '../kg-insight-content/KgInsightPreview';
import { TopTakeaways } from '../top-takeaways/TopTakeaways';
import styles from './CatalystWidgetInsightContent.module.scss';

type CatalystWidgetInsightContentProps = {
  chartActionTrackingSource?: PosthogTrackingSource;
  chartHeaderVariant: CatalystMarketReactionChartHeaderVariant;
  kgInsight: KgInsight;
  navigateToKGInsight: () => void;
  showDrawerHeader?: boolean;
};

export const CatalystWidgetInsightContent = ({
  chartActionTrackingSource,
  chartHeaderVariant,
  kgInsight,
  navigateToKGInsight,
  showDrawerHeader = false,
}: CatalystWidgetInsightContentProps) => {
  const { t } = useTranslation([
    'insights',
    'earnings',
    'whatsNew',
    'stream',
    'widget',
    'countries',
  ]);
  const { params } = useQueryParams();
  const { trackCatalystDrawerOpen } = usePosthogEvents();

  const entityTag = params[queryKeys.entityTag] ?? undefined;
  const themeId = params[queryKeys.themeId] ?? undefined;
  const utmSource = params[queryKeys.utmSource] ?? undefined;
  const isExpanded = params[queryKeys.drawerExpanded] === EXPANDED_VALUE;

  useTrackEmailDrawerOpen({
    isDrawerOpen: true,
    utmSource,
    trackingKey: getCatalystTrackingKey({
      insightId: kgInsight.id,
      category: kgInsight.category,
      entityTag,
      themeId,
    }),
    onTrackEmailOpen: () => {
      const { type, entities, themeIds } = getCatalystEmailDrawerTracking({
        category: kgInsight.category,
        insightId: kgInsight.id,
        entityTag,
        themeId,
      });

      trackCatalystDrawerOpen({
        source: POSTHOG_TRACKING_SOURCES.EMAIL,
        type,
        insightId: kgInsight.id,
        entities,
        themeIds,
      });
    },
  });

  const insightPreview = (
    <KgInsightPreview
      kgInsight={kgInsight}
      hideCatalystBadge={isExpanded}
      hideMetadataHeader={showDrawerHeader}
      keyStatsItemsPerRow={3}
      chartHeaderVariant={chartHeaderVariant}
      chartActionTrackingSource={chartActionTrackingSource}
    >
      <TopTakeaways
        kgInsight={kgInsight}
        source={
          kgInsight.category === 'company_catalyst'
            ? POSTHOG_TRACKING_SOURCES.CATALYST_INSIGHT_DRAWER
            : POSTHOG_TRACKING_SOURCES.MARKET_CATALYST_DRAWER
        }
      />
      <Button
        onClick={navigateToKGInsight}
        fullWidth
        label={t('insights:catalystGeneration.readFullCatalyst')}
        data-testid="read-full-catalyst"
      />
    </KgInsightPreview>
  );

  if (!showDrawerHeader) {
    return insightPreview;
  }

  return (
    <div
      className={clsx(styles.content, {
        [styles.collapsedContent]: !isExpanded,
      })}
    >
      <InsightDrawerHeaderContent
        cardType={CardType.CompanyCatalyst}
        context={kgInsight.subtitle}
        publishedAt={kgInsight.created_at}
        sentiment={kgInsight.sentiment}
        title={kgInsight.title}
      />
      {insightPreview}
    </div>
  );
};
